import { useEffect, useState } from 'react'
import { supabase } from '../../../../lib/supabase'

export default function GenderChart() {
  const [counts, setCounts] = useState({ male: 0, female: 0, other: 0 })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      const clubId = 'a0000000-0000-0000-0000-000000000001'

      const [{ count: male }, { count: female }, { count: other }] = await Promise.all([
        supabase.from('supporters').select('*', { count: 'exact', head: true }).eq('club_id', clubId).eq('gender', 'male'),
        supabase.from('supporters').select('*', { count: 'exact', head: true }).eq('club_id', clubId).eq('gender', 'female'),
        supabase.from('supporters').select('*', { count: 'exact', head: true }).eq('club_id', clubId).eq('gender', 'other'),
      ])

      setCounts({ male: male || 0, female: female || 0, other: other || 0 })
      setLoading(false)
    }
    fetchData()
  }, [])

  const total = counts.male + counts.female + counts.other || 1
  const malePct = Math.round((counts.male / total) * 100)
  const femalePct = Math.round((counts.female / total) * 100)
  const otherPct = 100 - malePct - femalePct

  // SVG donut values — circle circumference at r=15.9 is ~99.9 ≈ 100
  const maleArc = malePct
  const femaleOffset = malePct
  const femaleArc = femalePct
  const otherOffset = malePct + femalePct
  const otherArc = otherPct

  const data = [
    { label: 'Male', percentage: malePct, color: 'bg-green-500', stroke: '#16a34a' },
    { label: 'Female', percentage: femalePct, color: 'bg-blue-500', stroke: '#3b82f6' },
    ...(counts.other > 0 ? [{ label: 'Other', percentage: otherPct, color: 'bg-purple-500', stroke: '#a855f7' }] : []),
  ]

  const dominant = data.reduce((a, b) => (a.percentage >= b.percentage ? a : b), data[0])

  return (
    <div className="bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-6 h-full">
      <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-6">
        Gender Distribution
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-24 text-gray-400 text-sm">Loading...</div>
      ) : (
        <>
          <div className="flex justify-center mb-6">
            <div className="relative w-24 h-24">
              <svg viewBox="0 0 36 36" className="w-24 h-24 -rotate-90">
                {/* Background track */}
                <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e5e7eb" strokeWidth="3.5" className="dark:stroke-white/10" />
                {/* Other segment */}
                {counts.other > 0 && (
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="#a855f7" strokeWidth="3.5"
                    strokeDasharray={`${otherArc} 100`}
                    strokeDashoffset={`-${otherOffset}`} />
                )}
                {/* Female segment */}
                <circle cx="18" cy="18" r="15.9" fill="none" stroke="#3b82f6" strokeWidth="3.5"
                  strokeDasharray={`${femaleArc} 100`}
                  strokeDashoffset={`-${femaleOffset}`} />
                {/* Male segment */}
                <circle cx="18" cy="18" r="15.9" fill="none" stroke="#16a34a" strokeWidth="3.5"
                  strokeDasharray={`${maleArc} 100`} />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="font-black text-lg text-gray-900 dark:text-white">{dominant?.percentage}%</div>
                <div className="text-xs text-gray-500">{dominant?.label}</div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            {data.map((item) => (
              <div key={item.label} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-2.5 h-2.5 rounded-sm ${item.color}`} />
                  <span className="text-sm text-gray-500 dark:text-gray-400">{item.label}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400">{counts[item.label.toLowerCase()]}</span>
                  <span className="font-bold text-sm text-gray-900 dark:text-white">{item.percentage}%</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}